import { Room, RoomEvent, Track, type RemoteTrack, type RemoteParticipant } from 'livekit-client';
import type { MediaKey } from './api';

/**
 * The media room (G2): everyone's camera and sound, carried by the media
 * server the API handed us a key for. One small wrapper so the green room
 * and the host console attach tracks the same way.
 */
export interface Seat {
  identity: string;
  name: string;
  element: HTMLVideoElement | null;
  /** SN2: their shared screen, when they share one. */
  screen: HTMLVideoElement | null;
  /** G7: what the token said about them — role, colour, initials, camera choice. */
  meta: SeatMeta;
}
export interface SeatMeta { kind?: string; initials?: string; role?: string; location?: string; handle?: string; pronouns?: string; hue?: number; cameraOff?: boolean }

export function parseMeta(raw: string | undefined | null): SeatMeta {
  try { const v = JSON.parse(raw || '{}'); return v && typeof v === 'object' ? v as SeatMeta : {}; } catch { return {}; }
}

export type OnSeats = (seats: Seat[]) => void;

/**
 * G10: the room carries small messages beside the pictures — the host console
 * tells the stage which comment is on the screen. `onData` gets the raw bytes
 * and who sent them; the stage only listens to the host.
 */
export type OnData = (bytes: Uint8Array, from: string | null) => void;

export async function joinMedia(key: MediaKey, onSeats: OnSeats, opts: { publish: boolean; onData?: OnData }): Promise<Room> {
  const room = new Room({ adaptiveStream: true, dynacast: true });
  const seats = new Map<string, Seat>();
  const emit = () => onSeats([...seats.values()]);

  const seatFor = (p: RemoteParticipant): Seat => {
    let s = seats.get(p.identity);
    if (!s) { s = { identity: p.identity, name: p.name || p.identity, element: null, screen: null, meta: parseMeta(p.metadata) }; seats.set(p.identity, s); }
    return s;
  };
  room
    .on(RoomEvent.ParticipantConnected, (p) => { seatFor(p); emit(); })
    .on(RoomEvent.ParticipantDisconnected, (p) => { seats.delete(p.identity); emit(); })
    .on(RoomEvent.TrackSubscribed, (track: RemoteTrack, pub, p) => {
      const s = seatFor(p);
      if (track.kind === Track.Kind.Video) {
        const el = track.attach() as HTMLVideoElement;
        el.muted = true; el.playsInline = true;
        if (pub.source === Track.Source.ScreenShare) s.screen = el; else s.element = el;
      } else {
        track.attach();       // sound plays through a detached element
      }
      emit();
    })
    .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, pub, p) => {
      track.detach().forEach((el) => el.remove());
      const s = seats.get(p.identity);
      if (s && track.kind === Track.Kind.Video) { if (pub.source === Track.Source.ScreenShare) s.screen = null; else s.element = null; emit(); }
    })
    .on(RoomEvent.DataReceived, (payload, participant) => { opts.onData?.(payload, participant?.identity ?? null); })
    .on(RoomEvent.Disconnected, () => { seats.clear(); emit(); });

  await room.connect(key.url, key.token);
  room.remoteParticipants.forEach((p) => seatFor(p));
  emit();
  if (opts.publish) {
    try { await room.localParticipant.enableCameraAndMicrophone(); }
    catch { /* no camera: they can still hear and be heard on whatever they have */ }
  }
  return room;
}

/** SN2: share the screen into the room (with the tab's sound when there is one), or stop. */
export async function shareScreen(room: Room, on: boolean): Promise<boolean> {
  const pub = await room.localParticipant.setScreenShareEnabled(on, { audio: true });
  return on ? !!pub : false;
}

/** Send a message to everyone in the room, reliably — the stage among them. */
export async function sendToRoom(room: Room, bytes: Uint8Array): Promise<void> {
  const copy = new Uint8Array(new ArrayBuffer(bytes.byteLength)); copy.set(bytes);
  await room.localParticipant.publishData(copy, { reliable: true });
}

/** Mount a participant's video element into a box, once. */
export function mount(box: HTMLElement | null, el: HTMLVideoElement | null): void {
  if (!box) return;
  if (el && el.parentElement !== box) { box.replaceChildren(el); el.style.width = '100%'; el.style.height = '100%'; el.style.objectFit = 'cover'; }
  if (!el) box.replaceChildren();
}

